import { call, put, select, takeEvery } from 'redux-saga/effects';
import { RequestStatus } from 'constants';
import {
  SEARCH_ACTORS,
  GET_ALL_ACTORS,
  GET_TEMPLATE_ACTORS,
  SET_ACTORS_REQ_STATUS,
  CREATE_ACTOR_FILTER,
  UPDATE_ACTOR_FILTER,
  GET_ACTOR_FILTERS,
  SEARCH_ACTOR_FILTERS,
  REMOVE_ACTOR_FILTER,
  GET_FILTER,
} from '@control-front-end/common/constants/graphActors';
import { WS_CREATE_REACTION } from '@control-front-end/common/constants/reactions';
import api from '@control-front-end/common/sagas/api';
import AppUtils from '@control-front-end/utils/utils';
import { extendModelWithAvatars } from './graph/graphHelpers';

/**
 * Группировка акторов по title
 */
function groupActorsByTitle(items) {
  items.forEach((i, index) => {
    if (i.title.length > 500) return;
    const item = { ...i };
    if (index === 0) return;
    const prevItem = items[index - 1];
    const sim = AppUtils.similarText(prevItem.title, item.title, true);
    if (sim < 50) return;
    if (!prevItem.folderId) prevItem.folderId = prevItem.id;
    item.folderId = prevItem.folderId;
    items.splice(index, 1, item);
  });
}

/**
 *  Поиск акторов
 */
function* searchActors({ payload, callback }) {
  const accounts = yield select((state) => state.accounts);
  const accId = accounts.active;
  const { query, params, localState } = payload;
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/actors_filters/search/${accId}/${encodeURIComponent(query)}`,
    queryParams: { ...params },
  });
  if (result !== RequestStatus.SUCCESS) return;
  for (const obj of data.data.list) {
    obj.actorId = obj.id;
    yield call(extendModelWithAvatars, obj);
  }
  if (!localState) {
    yield put({
      type: SEARCH_ACTORS.SUCCESS,
      payload: {
        list: data.data.list,
        total: data.data.list.length,
      },
    });
  }
  if (callback) callback(data.data.list);
}

/**
 *  Фильтр акторов по темплейту
 */
function* getActorsByTemplate({ payload, callback }) {
  const {
    formId,
    loadMore,
    filter,
    orderBy,
    limit: customLimit,
    localState,
    groupByTittle = false,
  } = payload;
  let actorsState;
  if (localState) {
    actorsState = localState;
  } else {
    actorsState = yield select((state) => state.actorsList);
  }
  const { reqStatus, list, limit: defaultLimit, offset, endList } = actorsState;
  if (reqStatus !== RequestStatus.SUCCESS || endList) return;
  const limit = customLimit || defaultLimit;
  const starred = payload.starred || actorsState.starred;
  // Ставим флаг прогресса загрузки
  yield put({ type: SET_ACTORS_REQ_STATUS, payload: 'inProgress' });
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/actors_filters/${formId}`,
    queryParams: { limit, offset, orderBy, starred, ...filter },
  });
  // Ставим флаг окончания загрузки
  yield put({ type: SET_ACTORS_REQ_STATUS, payload: 'success' });
  if (result !== RequestStatus.SUCCESS) return;
  for (const obj of data.data.list) {
    yield call(extendModelWithAvatars, obj);
  }
  const newActorsList = loadMore ? structuredClone(list) : [];
  newActorsList.push(...data.data.list);
  newActorsList.forEach((i) => {
    i.actorId = i.id;
  });
  if (groupByTittle) groupActorsByTitle(newActorsList);
  if (localState && callback) {
    callback({
      ...localState,
      list: newActorsList,
      offset: offset + limit,
      endList: !data.data.length,
      formId,
      filter,
    });
    return;
  }
  const total = data.data.total;
  yield put({
    type: GET_TEMPLATE_ACTORS.SUCCESS,
    payload: {
      list: newActorsList,
      limit,
      offset: offset + limit,
      endList: newActorsList.length === total,
      total,
      formId: +formId,
      filter,
    },
  });
  if (callback) callback(data.data.list);
}

/**
 *  Фильтр доступных акторов пользователю отсортированных по шаблону
 */
function* getAllActors({ payload, callback }) {
  const { loadMore } = payload;
  const accounts = yield select((state) => state.accounts);
  const accId = accounts.active;
  const actorsState = yield select((state) => state.actorsList);
  const { reqStatus, list, limit, offset, endList } = actorsState;
  if (reqStatus !== RequestStatus.SUCCESS || endList) return;
  // Ставим флаг прогресса загрузки
  yield put({ type: SET_ACTORS_REQ_STATUS, payload: 'inProgress' });
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/actors_filters/actors/${accId}`,
    queryParams: { limit, offset },
  });
  // Ставим флаг окончания загрузки
  yield put({ type: SET_ACTORS_REQ_STATUS, payload: 'success' });
  if (result !== RequestStatus.SUCCESS) return;
  for (const obj of data.data) {
    yield call(extendModelWithAvatars, obj);
  }
  const newActorsList = loadMore ? list.concat(data.data) : data.data;
  newActorsList.forEach((i) => {
    i.actorId = i.id;
  });
  yield put({
    type: GET_TEMPLATE_ACTORS.SUCCESS,
    payload: {
      list: newActorsList,
      offset: offset + limit,
      endList: !data.data,
    },
  });
  if (callback) callback(data.data.list);
}

/**
 *  Сохранение фильтра для акторов
 */
function* createFilter({ payload, callback, formActions }) {
  const accounts = yield select((state) => state.accounts);
  const accId = accounts.active;
  const { result, data } = yield call(api, {
    method: 'post',
    url: `/actors_filters/${accId}`,
    body: payload,
  });
  if (result !== RequestStatus.SUCCESS) return;
  if (formActions) formActions.onClose();
  const { list } = yield select((state) => state.actorsFilters);
  const newList = list.concat([data.data]);
  yield put({
    type: CREATE_ACTOR_FILTER.SUCCESS,
    payload: { list: newList },
  });
  if (callback) callback(data.data);
}

/**
 *  Обновление фильтра для акторов
 */
function* updateFilter({ payload, callback, formActions }) {
  const { id } = payload;
  const { result, data } = yield call(api, {
    method: 'put',
    url: `/actors_filters/${id}`,
    body: payload,
  });
  if (result !== RequestStatus.SUCCESS) return;
  if (formActions) formActions.onClose();
  const { list } = yield select((state) => state.actorsFilters);
  const newList = list.map((i) => (i.id === id ? payload : i));
  yield put({
    type: UPDATE_ACTOR_FILTER.SUCCESS,
    payload: { list: newList },
  });
  if (callback) callback(data.data);
}

/**
 *  Удаление фильтра
 */
function* removeFilter({ payload, callback, formActions }) {
  const { id } = payload;
  const { result, data } = yield call(api, {
    method: 'delete',
    url: `/actors_filters/${id}`,
  });
  if (result !== RequestStatus.SUCCESS) return;
  if (formActions) formActions.onClose();
  const { list } = yield select((state) => state.actorsFilters);
  const newList = list.filter((i) => i.id !== id);
  yield put({
    type: REMOVE_ACTOR_FILTER.SUCCESS,
    payload: { list: newList },
  });
  if (callback) callback(data.data);
}

/**
 *  Получение сохраненных фильтров для акторов
 */
function* getFilters({ payload, callback }) {
  const accounts = yield select((state) => state.accounts);
  const accId = accounts.active;
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/actors_filters/list/${accId}`,
    body: payload,
  });
  if (result !== RequestStatus.SUCCESS) return;
  yield put({
    type: GET_ACTOR_FILTERS.SUCCESS,
    payload: {
      list: data.data,
    },
  });
  if (callback) callback(data.data);
}

/**
 * Получить фильтр по id
 */
function* getFilter({ payload, callback, errorCallback }) {
  const handleErrCodes = [403, 404];
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/actors_filters/get/${payload}`,
    handleErrCodes,
  });
  if (callback && result === RequestStatus.SUCCESS) {
    callback(data.data);
  } else if (errorCallback && handleErrCodes.includes(data.statusCode)) {
    errorCallback();
  }
  yield put({
    type: GET_FILTER.SUCCESS,
    payload: data.data,
  });
}

/**
 * Поиск фильтра по названию
 */
function* searchFilters({ payload, callback }) {
  const accounts = yield select((state) => state.accounts);
  const accId = accounts.active;
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/actors_filters/search_filter/${accId}/${payload}`,
  });
  if (result !== RequestStatus.SUCCESS) return;
  yield put({
    type: SEARCH_ACTOR_FILTERS.SUCCESS,
    payload: {
      list: data.data,
    },
  });
  if (callback) callback(data.data);
}

/**
 * Новая реакция по WS
 */
function* wsNewReaction({ payload }) {
  const { active: accId } = yield select((state) => state.accounts);
  if (payload.model.accId !== accId) return;
  const reaction = { ...payload.model };
  const { list, formId } = yield select((state) => state.actorsList);
  const systemForms = yield select((state) => state.systemForms);
  const copyList = list.slice();
  const { treeInfo, updatedAt } = reaction;
  if (systemForms.events.id !== formId || !copyList.length) return;
  // Если в текущем списке найдено событие, то просто сортируем список
  // иначе делаем запрос на получение события и добавялем его вверх, а последнее событие удаляем
  const indexActor = copyList.findIndex((i) => i.id === treeInfo.rootActorId);
  let actorM = {};
  if (indexActor !== -1) {
    actorM = copyList.splice(indexActor, 1);
    actorM = { ...actorM[0] };
    actorM.updatedAt = updatedAt;
    actorM.lastReaction = reaction;
    actorM.reactionsCount += 1;
    copyList.unshift(actorM);
  } else {
    const { result, data } = yield call(api, {
      method: 'get',
      url: `/actors_graph/${treeInfo.rootActorId}`,
      queryParams: {
        lastReaction: true,
        reactionsStats: true,
        reactionsCount: true,
      },
      handleErrCodes: [403, 404],
    });
    if (result !== RequestStatus.SUCCESS) return;
    yield call(extendModelWithAvatars, data.data);
    copyList.pop();
    copyList.unshift(data.data);
  }
  yield put({ type: GET_TEMPLATE_ACTORS.SUCCESS, payload: { list: copyList } });
}

function* actorsFilters() {
  yield takeEvery(SEARCH_ACTORS.REQUEST, searchActors);
  yield takeEvery(GET_TEMPLATE_ACTORS.REQUEST, getActorsByTemplate);
  yield takeEvery(GET_ALL_ACTORS.REQUEST, getAllActors);
  yield takeEvery(CREATE_ACTOR_FILTER.REQUEST, createFilter);
  yield takeEvery(UPDATE_ACTOR_FILTER.REQUEST, updateFilter);
  yield takeEvery(REMOVE_ACTOR_FILTER.REQUEST, removeFilter);
  yield takeEvery(GET_ACTOR_FILTERS.REQUEST, getFilters);
  yield takeEvery(GET_FILTER.REQUEST, getFilter);
  yield takeEvery(SEARCH_ACTOR_FILTERS.REQUEST, searchFilters);
  yield takeEvery(WS_CREATE_REACTION, wsNewReaction);
}

export default actorsFilters;
